import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../lib/auth'
import { localDraftStore } from '../lib/draft-storage'
import { draftsApi, type LogDraft } from './drafts-api'

/** The signed-in user's unfinished entries on the given farms, newest first. */
export function useLogDrafts(orgId: string | undefined, farmIds: string[]) {
  const { user } = useAuth()
  const [drafts, setDrafts] = useState<LogDraft[]>([])
  const [loading, setLoading] = useState(true)
  // An array literal is a new object every render; its contents are what matter.
  const key = farmIds.join(',')

  const reload = useCallback(async () => {
    const ids = key ? key.split(',') : []
    if (!orgId || ids.length === 0) {
      setDrafts([])
      setLoading(false)
      return
    }
    // A farm the user cannot read just contributes nothing.
    const lists = await Promise.all(ids.map((id) => draftsApi.list(orgId, id).catch(() => [] as LogDraft[])))
    setDrafts(lists.flat().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)))
    setLoading(false)
  }, [orgId, key])

  useEffect(() => {
    void reload()
  }, [reload])

  async function discard(draft: LogDraft) {
    if (!orgId) return
    await draftsApi.remove(orgId, draft.farmId, draft.id)
    if (user) localDraftStore(user.id).clear(draft.id)
    setDrafts((prev) => prev.filter((d) => d.id !== draft.id))
  }

  return { drafts, loading, discard, reload }
}
